import { Download } from "lucide-react";
import { toast } from "sonner";
import { usePwaInstall } from "@/hooks/usePwaInstall";

/**
 * Compact "Install" pill for the Navbar. Powered by the shared
 * ``usePwaInstall`` hook so every install affordance on the site (this
 * pill, the floating chip, the mobile-app section button, the iOS
 * hint modals) reads from the same source of truth.
 *
 * Hidden when:
 *   • the app is already running in standalone mode (nothing to install)
 *   • the browser hasn't fired ``beforeinstallprompt`` and we aren't on
 *     iOS Safari / iOS Chrome — nothing useful to do.
 *
 * Icon-only on very tight viewports, icon + label from ``xs`` up.
 */
export default function NavInstallPill() {
  const { canInstall, isStandalone, platform, install } = usePwaInstall();

  if (isStandalone || !canInstall) return null;

  const onClick = async () => {
    const outcome = await install();
    if (outcome === "accepted") toast.success("Cinevoya added to your home screen.");
    else if (outcome === "unavailable") toast.info("Open Cinevoya in Chrome, Edge or Safari to install as an app.");
  };

  const label =
    platform === "ios-safari" ? "Add to Home" : platform === "ios-chrome" ? "Install" : "Install";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Install Cinevoya on this device"
      className="group inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-terracotta/50 bg-terracotta/10 hover:bg-terracotta/20 text-terracotta text-xs font-semibold transition-colors"
      data-testid="nav-install-pill"
    >
      <Download className="w-3.5 h-3.5 group-hover:-translate-y-0.5 transition-transform" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
